import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Database, Server, Shield } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";

const DeveloperMode = () => {
  const [health, setHealth] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [h, a] = await Promise.all([
        api.get("/developer/health"),
        api.get("/developer/audit-recent", { params: { limit: 50 } }),
      ]);
      setHealth(h.data);
      setAudit(a.data);
    } catch (err) {
      if (err.response?.status !== 403) toast.error("Could not load developer data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 text-champagne animate-spin" /></div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="mb-6">
        <p className="label-eyebrow">Restricted</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Developer Mode</h1>
        <p className="text-textmuted mt-3 max-w-2xl leading-relaxed">
          System health, integration status, recent activity. Read-only. Code changes still go
          through the developer ticket queue.
        </p>
      </section>

      {health && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="regal-card p-4">
              <div className="flex items-center gap-2 mb-2"><Database className="w-4 h-4 text-champagne" strokeWidth={1.5} /><span className="label-eyebrow">MongoDB</span></div>
              <p className={`font-sans text-2xl font-light ${health.mongo === "ok" ? "text-[#A3C293]" : "text-[#E5A09E]"}`}>{health.mongo}</p>
            </div>
            <div className="regal-card p-4">
              <div className="flex items-center gap-2 mb-2"><Server className="w-4 h-4 text-champagne" strokeWidth={1.5} /><span className="label-eyebrow">App version</span></div>
              <p className="font-mono text-sm text-textmain">{health.app_version}</p>
            </div>
            <div className="regal-card p-4">
              <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-champagne" strokeWidth={1.5} /><span className="label-eyebrow">Server time</span></div>
              <p className="font-mono text-xs text-textmuted">{health.server_time?.slice(0, 19).replace("T", " ")}</p>
            </div>
            <div className="regal-card p-4">
              <span className="label-eyebrow">Collections</span>
              <p className="font-sans text-2xl font-light mt-1">{Object.keys(health.collections || {}).length}</p>
            </div>
          </div>

          <h2 className="font-heading text-2xl mb-3">Collection counts</h2>
          <div className="regal-card overflow-hidden mb-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {Object.entries(health.collections || {}).map(([name, count]) => (
                <div key={name} className="px-4 py-3 border border-seam/50 flex items-center justify-between">
                  <span className="text-xs font-mono text-textmuted truncate">{name}</span>
                  <span className="text-sm text-textmain font-mono">{count == null ? "—" : count}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <h2 className="font-heading text-2xl mb-3">Recent audit events</h2>
      <div className="regal-card overflow-hidden max-h-[420px] overflow-y-auto">
        <div className="divide-y divide-seam">
          {audit.map((a) => (
            <div key={a.id} className="px-4 py-2 text-xs font-mono flex items-center justify-between hover:bg-elevated/40">
              <span className="text-champagne">{a.action}</span>
              <span className="text-textmuted">{a.target_type}</span>
              <span className="text-textmuted">{a.created_at?.slice(0, 19).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default DeveloperMode;
